/* eslint-disable @typescript-eslint/no-unused-vars, no-param-reassign */

import {
  config,
  State as AuthState,
  setActions,
  saga as authSaga,
  updateConfig,
  getLocalState,
  Action,
  FETCH_ME_AFTER_SUCCESS_AUTHORIZATION, createUrl, reducers, updateStorageSaga, refreshTokenSaga,
} from '@triare/auth-redux';
import {
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import {
  spawn, takeLatest, put, select,
  call,
} from 'redux-saga/effects';
import { EnhancedStore } from '@reduxjs/toolkit/src/configureStore';
import { useSelector } from 'react-redux';
import { OTPResponse } from '@triare/auth-redux/src/saga/auth/OTP';
import { SignInAction } from '@triare/auth-redux/src/saga/auth/signIn';
import { UrlObject } from '@triare/auth-redux/src/config';
import { AnyObject } from '@triare/auth-redux/src/saga/common';
import { User as AuthUser } from '@triare/auth-redux/src/saga/auth/useMe';
import { userMeRequestSaga } from '@triare/auth-redux/dist/saga/auth/useMe';
import { RootState } from '../index';
import { SubCategory } from '../../hooks/api/createaccount';
import { CategoryShort } from '../category';
import { FileData } from '../types';
import { Business } from '../business';

export * from '@triare/auth-redux';

export default updateConfig({
  fetchDelay: Number.parseInt(process.env.REACT_APP_FETCH_DELAY || '0', 10),
  api: {
    url: process.env.REACT_APP_API || '',
  },
  displayName: 'Tipping Guide',
  name: 'Tipping Guide',
  signIn: {
    ...config.signIn,
    requestBody: {
      byEmail: ({ remember, ...data }: SignInAction) => JSON.stringify(data),
      byUsername: ({ remember, ...data }: SignInAction) => JSON.stringify(data),
      byPhone: ({ remember, ...data }: SignInAction) => JSON.stringify(data),
      byService: ({ method, remember, ...data }: SignInAction) => JSON.stringify(data),
    },
    fetchMeAfterSuccess: FETCH_ME_AFTER_SUCCESS_AUTHORIZATION,
  },
  OTP: {
    ...config.OTP,
    fetchMeAfterSuccess: false,
  },

  createUrl: (
    url: string | UrlObject,
    addToUrl?: string,
    payload?: PayloadAction,
    data?: AnyObject,
    _query?: AnyObject,
  ) => createUrl(url, addToUrl, payload, data, {
    ..._query,
    // lang: 'en',
    site: 'user_portal',
  }),
});

export const moduleName = config.auth.name;

export interface User extends AuthUser {
  name: string;
  createdAt?: string;
  description?: string;
  email?: string;
  phone: string;
  photo?: FileData | null;
  settings?: {
    isEmailVerified: boolean;
    isPhoneVerified: boolean;
  };
  country?: {
    id: number;
    iso3: string;
    name: string;
  } | null;
  countryId: number | null;
  zipCode?: string;
  subCategory?: SubCategory;
  category?: CategoryShort;
  businesses?: Business[];
  yearBirth?: number;
}

export interface State extends AuthState {
  user: User | null;
  secretKey: string;
}

export function useAuth(): State {
  return useSelector((state: RootState) => state[moduleName]);
}

export const auth = createSlice({
  name: moduleName,
  initialState: getLocalState(),
  reducers: {
    ...reducers,
    signInSuccess: (state, { payload }) => {
      if (payload.access) state.access = payload.access;
      if (payload.refresh) state.refresh = payload.refresh;

      state.signIn.loading = false;
      state.signIn.response = payload || null;
      state.authorized = !!(payload.access && payload.refresh);

      if (payload?.remember !== undefined) {
        state.remember = payload?.remember;
      }

      if (payload.user) {
        state.user = payload.user;
      }
    },
    updateUser: (state, { payload }: PayloadAction<Partial<User>>) => {
      state.user = state.user ? { ...state.user, ...payload } : { ...payload } as User;
    },
    updateUserRequest: (state, { payload }: PayloadAction<Partial<User>>) => {
      // This is just a trigger action, no state change here
    },
  },
});

export const { actions } = auth;

setActions(actions);

function* selectAuthState() {
  return (yield select((state: RootState) => state[moduleName])) as State;
}

function* handleConfirmSuccessSaga() {
  yield call(updateStorageSaga); // Update storage with new tokens
  // yield call(refreshTokenSaga);
  yield call(userMeRequestSaga); // Fetch user after confirm-success.
}

function* updateUserSaga({ payload }: PayloadAction<Partial<User>>) {
  try {
    yield put(actions.updateUser(payload));

    yield call(updateStorageSaga);

    yield call(userMeRequestSaga);
  } catch (error) {
    console.error('Update user saga error:', error);
  }
}

export function* saga(store: EnhancedStore) {
  yield spawn(authSaga, store);

  yield takeLatest(actions.updateUserRequest.toString(), updateUserSaga);

  yield takeLatest(actions.OTPSuccess.toString(), function* trigger({ payload }: Action<OTPResponse>) {
    yield put({
      type: actions.signInSuccess.toString(),
      payload,
    });
  });

  /** Updates storage with token after confirmSuccess */
  yield takeLatest([
    config.actions.confirmSuccess.toString(),
  ], handleConfirmSuccessSaga);

  /** If user-me fails - trigger refresh-token */
  yield takeLatest([
    config.actions.userMeError.toString(),
  ], refreshTokenSaga);

  // yield takeLatest(actions.userMeSuccess.toString(), function* trigger() {
  //   const authData = (yield getState()) as State;
  //
  //   if (authData && authData.user && authData.user.role === UserRoles.GUEST) {
  //     yield put(signOut());
  //
  //     window.location.href = '/sign-up';
  //   }
  // });
}
